/* eslint-disable jsx-a11y/label-has-associated-control */
import React from "react";
import utils from "@utils";
import PropTypes from "prop-types";
import { Loader, Image, Switch } from "../../../../../components";
import "../NonRKPayInvoiceDetail.scss";
import ConnectedEstimateLink from "./ConnectedEstimateLink";
import NonRKPayInvoiceDetailBalanceDisplay from "./NonRKPayInvoiceDetailBalanceDisplay";
import RefundsDisplay from "./RefundsDisplay";
import AvailableFundsDisplay from "./AvailableFundsDisplay";
import PaymentsAppliedDisplay from "./PaymentsAppliedDisplay";
import SectionSeparator from "./SectionSeparator";
import InvoiceDisclaimer from "./InvoiceDisclaimer";
import NonRKPayInvoiceItemView from "./NonRKPayInvoiceItem/NonRKPayInvoiceItemView";
import OfficerNonRKPayInvoiceItems from "./OfficerNonRKPayInvoiceItems";
import ConnectedInvoiceLink from "./ConnectedInvoiceLink";

const NonRKPayInvoiceDetailView = ({
	user,
	nonRKPayInvoiceDetailHook,
	clientAddress,
	officerMiscFeesV3,
}) => {
	const { lang } = global;

	const {
		invoiceState,
		loadingInvoice,
		isInvoicingV3,
		selectedClient,
		paymentHistory,
		excludeOfficerRate,
		loadingLineItems,
		refundsInTransit,
		toggleOfficerView,
		availableFunds,
		associatedInvoices,
		loadingEstimateAssociatedInvoices,
		nonrkpayDetailPrepayEnhancementsEnabled,
		initialized,
		toggleConnectedInvoicesModal,
	} = nonRKPayInvoiceDetailHook;

	const {
		voided,
		amended,
		disputed,
		issueDate,
		dueDate,
		isEstimate,
		invoiceNumber,
		invoiceDisclaimer,
		profilePicUrl,
		from,
		billTo,
		totals,
		officerViewList,
		lineItems,
		showFeesBreakdown,
		notesToClient,
		statusId,
		sentDateTimeUTC,
		finalized,
		estimateId,
		estimateNumber,
		archived,
		estimateFinalized,
		invoiceVersion
	} = invoiceState;

	const invoiceStatus = utils.mapNonRKPayInvoiceStatusIdToLabelType(
		statusId,
		voided,
		archived,
		estimateFinalized,
	);

	return (
		<div
			id="nonrkpay-invoice-detail-view"
			className="finalized-invoice"
		>
			<Loader active={loadingInvoice || !initialized} />
			<div className="row">
				<div className="col-xs-6">
					<span
						className={`label ${invoiceStatus.label === "Paid" ? "label-paid-dark-green" : `label-${invoiceStatus.type}`}`}
					>
						{invoiceStatus.label}
					</span>
				</div>
			</div>
			{disputed && (
				<div className="row">
					<div className="col-xs-6">
						<span className="label label-danger">{lang.t("Disputed")}</span>
					</div>
				</div>
			)}
			{amended && (
				<div className="row">
					<div className="col-xs-6">
						<span className="label label-success">{lang.t("Amended")}</span>
					</div>
				</div>
			)}
			{/* Invoice # & Dates */}
			<div className="row form-section">
				<div className="col-xs-6 col-md-5 col-lg-4">
					<div>
						<label>{`${lang.t(isEstimate ? "Estimate" : "Invoice")} #`}</label>
						<div className="visible-print-block">{invoiceNumber}</div>
					</div>
				</div>
				<div className="col-xs-6 col-md-5 col-lg-4 col-md-offset-2 col-lg-offset-4 text-right">
					<div className="row">
						<div className="col-xs-6 right-align">
							<label htmlFor="issueDate">{lang.t("Date Created")}</label>
							<div className="visible-print-block">
								{utils.formatDate(
									utils.parseDateCrossBrowser(issueDate),
									"en-US",
									undefined,
									"2-digit",
								)}
							</div>
						</div>
						<div className={`col-xs-6 right-align ${isEstimate ? "hidden" : ""}`}>
							<label htmlFor="dueDate">{lang.t("Due Date")}</label>
							<div className="visible-print-block">
								{utils.formatDate(
									utils.parseDateCrossBrowser(dueDate),
									"en-US",
									undefined,
									"2-digit",
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Estimate Associated Invoices */}
			<If condition={loadingEstimateAssociatedInvoices}>
				<div className="row form-section">
					<div className="col-xs-6 col-md-5 col-lg-4">
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</div>
				</div>
			</If>
			<ConnectedInvoiceLink
				associatedInvoices={associatedInvoices}
				isEstimate={isEstimate}
				toggleConnectedInvoicesModal={toggleConnectedInvoicesModal}
			/>
			{/* Invoice Associated Estimate */}
			<If condition={nonrkpayDetailPrepayEnhancementsEnabled}>
				<ConnectedEstimateLink
					estimateId={estimateId}
					estimateNumber={estimateNumber}
					rowClassName="row form-section"
					labelClassName="col-xs-6 col-md-5 col-lg-4"
				/>
			</If>
			{/* Invoice Notes to client and logo */}
			<div className="row form-section v-align">
				<div className="col-xs-12 col-sm-6 col-md-5 col-lg-4">
					<label htmlFor="notesToClient">{lang.t("Notes to Client")}</label>
					<div className="visible-print-block text-justify">
						{notesToClient.split("\n").map(text => (
							<span key={text.replace(/\s/g, "")}>
								{text}
								<br />
							</span>
						))}
					</div>
				</div>
				<div className="col-xs-12 col-sm-6 col-md-5 col-lg-4 col-md-offset-2 col-lg-offset-4">
					<div className="org-img">
						<Image
							className="img-responsive"
							src={profilePicUrl}
							fallbackSrc="avatar"
						/>
					</div>
				</div>
			</div>
			<div className="row form-section v-align">
				{/* Invoice sender details */}
				<div className="col-xs-12 col-sm-6 col-md-5 col-lg-4">
					<h4 className="bold-text">{lang.t("From")}</h4>
					<div className="form-group">
						<label htmlFor="fromName">{lang.t("Name")}</label>
						<div>{from.name}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromAddress">{lang.t("Department Address")}</label>
						<div>{from.address}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromPhone">{lang.t("Phone Number")}</label>
						<div>{utils.formatPhoneNumber(from.phone)}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromEmail">{lang.t("Email")}</label>
						<div>{from.email}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromContact">{lang.t("Contact")}</label>
						<div>{from.contact}</div>
					</div>
				</div>
				{/* Invoice recipient details */}
				<div className="col-xs-12 col-sm-6 col-md-5 col-lg-4 col-md-offset-2 col-lg-offset-4 invoice-bill-to">
					<h4 className="bold-text">Bill To</h4>
					<div className="form-group">
						<label htmlFor="selectedClient">{lang.t("Name")}</label>
						<div>{selectedClient.label}</div>
					</div>
					<div className="form-group">
						<label htmlFor="streetAddress">{lang.t("Business Address")}</label>
						<div>{clientAddress}</div>
					</div>
					<div className="form-group">
						<label htmlFor="billingPhone">{lang.t("Phone Number")}</label>
						<div>{utils.formatPhoneNumber(billTo.phone)}</div>
					</div>
					<div className="form-group">
						<label htmlFor="billingEmail">{lang.t("Email")}</label>
						<div>{billTo.email}</div>
					</div>
					<div className="form-group">
						<label htmlFor="billingContact">{lang.t("Contact")}</label>
						<div>{billTo.contact}</div>
					</div>
				</div>
			</div>
			<div className="row">
				<div className="col-xs-6">
					<Switch
						label={lang.t("Group by Officer")}
						on={officerViewList.length > 0}
						onClick={toggleOfficerView}
					/>
				</div>
			</div>
			<div className="separator" />
			<Choose>
				<When condition={officerViewList.length > 0}>
					{/* Officer grouped headers */}
					<div className="row job-headers hidden-xs hidden-sm">
						<div className="col-md-3">
							<strong>{lang.t("Officer")}</strong>
						</div>
						<div className="col-md-3">
							<strong>{lang.t("Job")}</strong>
						</div>
						<div className="col-md-3">
							<strong>
								{lang.t("Start Date")} &amp; {lang.t("Time")}
							</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Rate")}</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Hours")}</strong>
						</div>
						<div className="col-md-1 text-right print-amount">
							<strong>{lang.t("Total")}</strong>
						</div>
					</div>
					{/* Officer grouped items */}
					<div className="container-fluid table-body">
						<For
							each="officer"
							of={officerViewList}
						>
							<OfficerNonRKPayInvoiceItems
								user={user}
								officer={officer}
								lineItems={lineItems}
								nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
								invoiceNotEditable
							/>
						</For>
					</div>
				</When>
				<Otherwise>
					{/* Invoice line item headers */}
					<div className="row job-headers hidden-xs hidden-sm">
						<div className="col-md-1">
							<strong>{lang.t("Job ID")}</strong>
						</div>
						<div className="col-md-2">
							<strong>{lang.t("Job Name")}</strong>
						</div>
						<div className="col-md-2">
							<strong>
								{lang.t("Date")} &amp; {lang.t("Time")}
							</strong>
						</div>
						<div className="col-md-2">
							<strong>{lang.t("Officer")}</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Rate")}</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Hours")}</strong>
						</div>
						<div className="col-md-2">
							<strong>{lang.t("Fees")}</strong>
						</div>
						<div className="col-md-1 text-right print-amount">
							<strong>{lang.t("Total")}</strong>
						</div>
					</div>
					{/* Invoice line items */}
					<div className="container-fluid table-body">
						<For
							each="item"
							of={lineItems}
						>
							<NonRKPayInvoiceItemView
								user={user}
								item={item}
								nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
							/>
						</For>
						<If condition={loadingLineItems}>
							<div className="row text-center">
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</div>
						</If>
					</div>
				</Otherwise>
			</Choose>
			{/* Invoice summary */}
			<If condition={lineItems.length > 0}>
				<div className="row payment-spacer">
					<div className="col-xs-12 col-sm-5 col-sm-offset-7 text-right">
						<div className="row payment-spacer">
							<If condition={!excludeOfficerRate}>
								<div className="col-xs-9">
									<strong>{lang.t("Officer Hourly Total")}</strong>
								</div>
								<div className="col-xs-3">
									{utils.currencyFormatter(totals.hourlyTotal)}
								</div>
							</If>
							<If condition={excludeOfficerRate}>
								<div className="col-xs-9">
									<div className="exclude-fees">
										<strong style={{ color: "darkgrey" }}>
											{lang.t("Officer Pay Excluded")}
										</strong>
									</div>
								</div>
								<div className="col-xs-3">---</div>
							</If>
						</div>
						<Choose>
							<When condition={showFeesBreakdown}>
								<If condition={officerMiscFeesV3}>
									<div className="row payment-spacer">
										<div className="col-xs-9">
											<strong>{`${lang.t("Officer Fees")} ${lang.t("Total")}`}</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.miscPersonnelFeesTotal)}
										</div>
									</div>
								</If>
								<div className="row payment-spacer">
									<div className="col-xs-9">
										<strong>{`${lang.t("Agency")} ${lang.t("Fees Total")}`}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.agencyFeesTotal)}
									</div>
								</div>
								<div className="row payment-spacer">
									<div className="col-xs-9">
										<strong>{`${lang.t("Vehicle")} ${lang.t("Fees Total")}`}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.vehicleFeesTotal)}
									</div>
								</div>
								<div className="row payment-spacer">
									<div className="col-xs-9">
										<strong>{`${lang.t("Equipment")} ${lang.t("Fees Total")}`}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.equipmentFeesTotal)}
									</div>
								</div>
								<If condition={isInvoicingV3}>
									<div className="row payment-spacer">
										<div className="col-xs-9">
											<strong>{`${lang.t("Coordination")} ${lang.t("Fees Total")}`}</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.coordinationFeesTotal)}
										</div>
									</div>
								</If>
							</When>
							<Otherwise>
								<div className="row payment-spacer">
									<div className="col-xs-9">
										<strong>{lang.t("Fees Total")}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.feesTotal)}
									</div>
								</div>
							</Otherwise>
						</Choose>
						<If condition={totals.additionalFees.length > 0 || invoiceVersion === 3}>
							<SectionSeparator />
							<For
								each="fee"
								of={totals.additionalFees}
								index="index"
							>
								<div
									className="row"
									key={`additional_fee_${fee.Name}${fee.Total}`}
								>
									<div className="col-xs-9">
										<strong>{fee.Name}</strong>
									</div>
									<div className="col-xs-3">{utils.currencyFormatter(fee.Total)}</div>
								</div>
							</For>
							<If condition={invoiceVersion === 3}>
								<If condition={totals.subTotal !== null}>
									<div className="row payment-spacer">
										<div className="col-xs-9">
											<strong>
												{isEstimate
													? lang.t("Estimated Subtotal")
													: lang.t("Subtotal")}
											</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.subTotal)}
										</div>
									</div>
								</If>
								<If condition={totals.serviceFee !== null}>
									<div className="row payment-spacer">
										<div className="col-xs-9">
											<strong>
												{isEstimate
													? lang.t("Estimated Service Fee")
													: lang.t("Service Fee")}
											</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.serviceFee)}
										</div>
									</div>
								</If>
								<If
									condition={
										totals.workerCompensationFee !== null &&
										totals.workerCompensationFee > 0
									}
								>
									<div className="row payment-spacer">
										<div className="col-xs-9">
											<strong>
												{isEstimate
													? lang.t("Estimated Workers' Compensation Fee")
													: lang.t("Workers' Compensation Fee")}
											</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.workerCompensationFee)}
										</div>
									</div>
								</If>
								<If condition={totals.tax !== null}>
									<div className="row payment-spacer">
										<div className="col-xs-9">
											<strong>
												{isEstimate
													? lang.t("Estimated Tax")
													: lang.t("Service Tax")}
											</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.tax)}
										</div>
									</div>
								</If>
							</If>
						</If>
						<SectionSeparator />
						<div className="row">
							<div className="col-xs-9">
								<strong>
									{isEstimate ? lang.t("Estimated Total") : lang.t("Total")}
								</strong>
							</div>
							<div className="col-xs-3">
								{utils.currencyFormatter(totals.totalDue)}
							</div>
						</div>
						<PaymentsAppliedDisplay
							totals={totals}
							paymentHistory={paymentHistory}
							nonrkpayDetailPrepayEnhancementsEnabled={
								nonrkpayDetailPrepayEnhancementsEnabled
							}
							refundsInTransit={refundsInTransit}
						/>
						<SectionSeparator />
						<NonRKPayInvoiceDetailBalanceDisplay
							nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
						/>
						<SectionSeparator />
						{nonrkpayDetailPrepayEnhancementsEnabled && (
							<>
								{isEstimate && (
									<>
										<AvailableFundsDisplay availableFunds={availableFunds} />
										<SectionSeparator />
									</>
								)}
								{(totals.refunds < 0 || refundsInTransit?.length > 0) && (
									<>
										<RefundsDisplay
											totals={totals}
											paymentHistory={paymentHistory}
											refundsInTransit={refundsInTransit}
										/>
										<SectionSeparator />
									</>
								)}
							</>
						)}
						<InvoiceDisclaimer disclaimer={invoiceDisclaimer} />
					</div>
				</div>
				<If condition={excludeOfficerRate}>
					<div className="row">
						<div className="col-xs-12 col-sm-6 exlude-fees-alert">
							<div className="row">
								<div className="col-sm-12">
									<label
										htmlFor="attention"
										style={{ color: "red" }}
									>
										{lang.t("Attention")}
									</label>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-12">
									<label className="attention-message">
										{lang.t("Attention Exclude Pay TextBox")}
									</label>
								</div>
							</div>
						</div>
					</div>
				</If>
				<If
					condition={
						user.isSuperAdmin() &&
						((isEstimate && !sentDateTimeUTC) || (!isEstimate && !finalized))
					}
				>
					<div className="row hidden visible-print">
						<div className="col-xs-12">
							<div className="row">
								<div className="col-sm-12">
									<label
										htmlFor="attention"
										style={{ color: "red" }}
									>
										{lang.t("Attention")}
									</label>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-12">
									<label className="attention-message">
										{`${lang.t(isEstimate ? "PDF Disclaimer message for estimates not sent to clients" : "PDF Disclaimer message for invoices not sent to clients")}`}
									</label>
								</div>
							</div>
						</div>
					</div>
				</If>
			</If>
		</div>
	);
};

NonRKPayInvoiceDetailView.propTypes = {
	user: PropTypes.object,
	clientAddress: PropTypes.string,
	officerMiscFeesV3: PropTypes.bool,
	nonRKPayInvoiceDetailHook: PropTypes.object.isRequired,
};

NonRKPayInvoiceDetailView.defaultProps = {
	user: {},
	officerMiscFeesV3: false,
	clientAddress: "",
};

export default NonRKPayInvoiceDetailView;
